import { toast } from "@gigpro/ui/Toast";
import { pluralize } from "@gigpro/utils";
import {
  getHireJobsConvertibleShiftsRetrieveQueryKey,
  getHireJobsWorkingInterviewsRetrieveQueryKey,
  hireJobsWorkingInterviewConversionsCreate,
} from "@sidegigllc/api/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ShiftConversion } from "domains/hire/components/ConvertibleShiftList/seats";
import { toastApiError } from "utils/toastApiError";

type ConversionOutcome = { converted: number; errors: Array<unknown> };

/** One request per shift, in turn, so a shift that changed doesn't sink the rest. Never rejects. */
export const convertShifts = (jobId: string, conversions: Array<ShiftConversion>) =>
  conversions.reduce<Promise<ConversionOutcome>>(
    (previous, { gigId, seats }) =>
      previous.then((outcome) =>
        hireJobsWorkingInterviewConversionsCreate(jobId, { gig: gigId, seats }).then(
          () => ({ ...outcome, converted: outcome.converted + 1 }),
          (error: unknown) => ({ ...outcome, errors: [...outcome.errors, error] }),
        ),
      ),
    Promise.resolve({ converted: 0, errors: [] }),
  );

export const useConvertShiftsToWorkingInterviews = (jobId: string, onAllConverted: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversions: Array<ShiftConversion>) => convertShifts(jobId, conversions),
    onSuccess: ({ converted, errors }) => {
      if (errors.length === 0) {
        toast.success(`Converted ${converted} ${pluralize(converted, "shift")} to working interviews`);
        onAllConverted();
      } else if (converted === 0) {
        toastApiError(errors[0]);
      } else {
        toast.error(
          `Converted ${converted} of ${converted + errors.length} shifts. The rest changed, so check the list.`,
        );
      }
    },
    // A failed shift most likely changed since the list loaded.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: getHireJobsConvertibleShiftsRetrieveQueryKey(jobId) }),
        queryClient.invalidateQueries({ queryKey: getHireJobsWorkingInterviewsRetrieveQueryKey(jobId) }),
      ]),
  });
};
